import type { CSSProperties } from 'react';

import classNames from 'classnames';

import { generatePositionStyle, type PositionStyle } from '@/utils/positioning';

import { getTextAlignStyleVariables } from '../utils/typography';
import { useAnimatedBlock } from '../utils/useAnimatedBlock';

import { PARAGRAPH_DEFAULTS } from './Paragraph.constants';
import styles from './Paragraph.css';
import type { ParagraphElementProps } from './Paragraph.types';

// Keeping this as inherit could have unintended consequences with older templates so let's force to be transparent on paragraph elements
const getBackgroundColor = (backgroundColor: string) => {
  if (backgroundColor === 'inherit') return 'transparent';
  return backgroundColor;
};

export function ParagraphElement(props: ParagraphElementProps) {
  // remove isLive from rest props since we're spreading it on the div (which is frowned upon)
  const { element, children, attributes, className, isLive: _isLive, as, ...rest } = props;
  const {
    width,
    maxWidth,
    lineHeight,
    mobileLineHeight,
    textAlign,
    mobileTextAlign,
    animation,
    padding,
    mobilePadding,
    margin,
    customAttributes,
    backgroundColor,
    lineClampClass,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    textTransform,
    letterSpacing,
  } = element.attrs || {};

  const [marginTop, marginRight, marginBottom, marginLeft] = (margin ?? PARAGRAPH_DEFAULTS.margin)
    .toString()
    .split(' ');
  const [ref, animatedClassname] = useAnimatedBlock(animation);

  const Tag = as || 'p';

  /* spreading props on standard DOM elements causes React warnings and hydration errors */
  return (
    <Tag
      {...rest}
      ref={ref}
      {...customAttributes}
      className={classNames(
        className,
        animatedClassname,
        styles.paragraphElement,
        customAttributes?.className,
        lineClampClass,
      )}
      style={
        {
          ...(element?.attrs && generatePositionStyle(element.attrs as PositionStyle)),
          width,
          maxWidth,
          '--padding': padding ?? PARAGRAPH_DEFAULTS.padding,
          '--mobile-padding': mobilePadding ?? PARAGRAPH_DEFAULTS.padding,
          '--padding-root': padding ?? PARAGRAPH_DEFAULTS.paddingRoot,
          '--margin-top': marginTop,
          '--margin-right': marginRight,
          '--margin-bottom': marginBottom,
          '--margin-left': marginLeft,
          '--margin': margin ?? PARAGRAPH_DEFAULTS.margin,
          '--line-height': lineHeight,
          '--mobile-line-height': mobileLineHeight,
          '--background-color': backgroundColor ? getBackgroundColor(backgroundColor) : 'transparent',
          '--border-radius': borderRadius,
          '--border-width': borderWidth,
          '--border-color': borderColor,
          '--border-style': borderStyle,
          '--text-transform': textTransform,
          '--letter-spacing': letterSpacing ?? PARAGRAPH_DEFAULTS.letterSpacing,
          ...getTextAlignStyleVariables({
            mobileAlign: mobileTextAlign || textAlign,
            desktopAlign: textAlign,
          }),
        } as CSSProperties
      }
      {...attributes}
    >
      {children}
    </Tag>
  );
}
